import type { Aabb, WorldManifest } from "@townsy/contracts";
import { VEHICLE_STYLE_COUNT } from "@townsy/contracts";
import { districtItems } from "./districtItems";
import { VEHICLE_SPECS, driverDoorPoint, obbCorners, obbIntersectsObb, vehicleSpec, type Obb, type VehiclePose } from "./vehicles";

/**
 * Town traffic (ADR-0006: shared, server-simulated). Cars follow closed lane loops, yield to
 * pedestrians in their lane, keep a gap to the car ahead and to driven / parked cars (blockers),
 * WAIT while someone stands at their driver's door so that person can get in, and (world v3,
 * ADR-0008) take turns at junctions where two loops cross. Pure and deterministic: the realtime
 * street room steps it at 20 Hz; nothing here is ever persisted, owned, rewarded or counted in
 * statistics.
 *
 * Moved from apps/web/src/scene/town/traffic.ts (client-only ambient traffic of round 2).
 * Coordinates follow @townsy/world (x east, z north, yaw 0 = +z). Traffic drives on the right.
 */
export interface P2 {
  x: number;
  z: number;
}

/**
 * Where a loop crosses another loop (world v3): the arc-length range of CAR CENTRE positions on this
 * loop at which a car could touch a car of another loop in the same junction `zone`. A loop crossing
 * a junction twice (both directions of a lane) has two spans with the same zone.
 */
export interface JunctionSpan {
  /** Junction zone id, shared by every span (of any loop) whose cars could collide. */
  zone: number;
  /** Arc position where the span starts, in [0, length). */
  start: number;
  /** Arc position where it ends: start < end <= start + length (end > length wraps). */
  end: number;
  /**
   * Turn-taking group (world v4): cars of one group may be in the zone together, cars of another
   * group wait. Absent = the loop index (v3: the two directions of a lane share its crossing). On a
   * loop that meets ITSELF (the two directions of the Market Street loop at a corner, where the
   * inside and the outside lane are too close for two bodies) every span is its own group, so the two
   * directions take turns there exactly like two crossing roads.
   */
  group?: number;
}

export interface LoopPath {
  id: string;
  points: P2[];
  /** Cumulative arc length at each point (closed: last segment returns to points[0]). */
  cumulative: number[];
  length: number;
  /** Arc-length ranges where cars should slow down (U-turns). */
  slowZones: [number, number][];
  /**
   * Arc-length ranges of rounded street corners (world v4: where the Market Street loop turns between
   * an avenue and Market Street). Cars slow down there like in a U-turn, but unlike a U-turn the car
   * body stays on the road the whole way round.
   */
  corners?: [number, number][];
  /** Junction spans (set by trafficLoops / withJunctions; absent or empty = no crossings). */
  junctions?: JunctionSpan[];
}

function arc(cx: number, cz: number, r: number, a0: number, a1: number, steps: number): P2[] {
  const out: P2[] = [];
  for (let i = 1; i < steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    out.push({ x: cx + Math.cos(a) * r, z: cz + Math.sin(a) * r });
  }
  return out;
}

export function makeLoop(id: string, points: P2[], slowPoints: P2[][] = [], cornerPoints: P2[][] = []): LoopPath {
  const cumulative: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    cumulative.push(cumulative[i - 1]! + Math.hypot(b.x - a.x, b.z - a.z));
  }
  const last = points[points.length - 1]!;
  const first = points[0]!;
  const length = cumulative[cumulative.length - 1]! + Math.hypot(first.x - last.x, first.z - last.z);
  const zone = (pts: P2[]): [number, number] => {
    const idx = pts.map((p) => points.findIndex((q) => Math.abs(q.x - p.x) < 1e-6 && Math.abs(q.z - p.z) < 1e-6)).filter((i) => i >= 0);
    const s0 = cumulative[Math.min(...idx)]!;
    const s1 = cumulative[Math.max(...idx)]!;
    return [s0 - 6, s1 + 3];
  };
  const slowZones = slowPoints.map(zone);
  return cornerPoints.length ? { id, points, cumulative, length, slowZones, corners: cornerPoints.map(zone) } : { id, points, cumulative, length, slowZones };
}

/**
 * A two-lane loop along a straight road: the lane on the right of the forward direction from
 * (a) to (b), a U-turn at (b), the opposite lane back and a U-turn at (a).
 */
export function laneLoop(id: string, a: P2, b: P2, laneOffset: number): LoopPath {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  const fx = dx / len;
  const fz = dz / len;
  // Right of forward (x east, z north): facing +z, right is +x -> right = (fz, -fx).
  const rx = fz;
  const rz = -fx;
  const out: P2 = { x: a.x + rx * laneOffset, z: a.z + rz * laneOffset };
  const outEnd: P2 = { x: b.x + rx * laneOffset, z: b.z + rz * laneOffset };
  const backStart: P2 = { x: b.x - rx * laneOffset, z: b.z - rz * laneOffset };
  const backEnd: P2 = { x: a.x - rx * laneOffset, z: a.z - rz * laneOffset };
  // In (x, z) with angles atan2(z, x), rotating `right` by +90 degrees gives `forward`, so an arc
  // from angle(right) to angle(right) + PI around b bulges forward past b (the far U-turn), and
  // from angle(right) + PI to + 2 PI around a bulges backward past a.
  const angRight = Math.atan2(rz, rx);
  const turnB = arc(b.x, b.z, laneOffset, angRight, angRight + Math.PI, 8);
  const turnA = arc(a.x, a.z, laneOffset, angRight + Math.PI, angRight + Math.PI * 2, 8);
  const points = [out, outEnd, ...turnB, backStart, backEnd, ...turnA];
  return makeLoop(id, points, [[outEnd, ...turnB, backStart], [backEnd, ...turnA]]);
}

/**
 * Lane radius of a street loop's corners (world v4 Market Street corners: a 6 m road turning 90
 * degrees with square kerbs). The lane on the inside of the turn (a right turn: right-hand traffic)
 * turns tightly so its body stays clear of the inner kerb corner; the outside lane turns wider so its
 * body stays clear of the outer road edges. They are not concentric, so the two lanes come closer at
 * the corner than on the straights; traffic.test.ts checks every pair of bodies passing at a corner.
 */
export const STREET_CORNER_INSIDE_RADIUS = 2;
export const STREET_CORNER_OUTSIDE_RADIUS = 8;
/** Arc points per corner (a 90 degree corner: 3.75 degree chords, so a body's heading follows the arc closely). */
const CORNER_STEPS = 24;

/**
 * The lane `offset` metres right of the forward direction along an open polyline of straight streets:
 * the offset straight lines, each corner rounded with STREET_CORNER_INSIDE_RADIUS (right turns) or
 * STREET_CORNER_OUTSIDE_RADIUS (left turns). Returns the lane's points and, per corner, its arc points.
 */
function offsetLane(centre: readonly P2[], offset: number): { points: P2[]; corners: P2[][] } {
  const dirs: P2[] = [];
  for (let i = 0; i + 1 < centre.length; i++) {
    const a = centre[i]!;
    const b = centre[i + 1]!;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    dirs.push({ x: (b.x - a.x) / len, z: (b.z - a.z) / len });
  }
  // Right of forward (x east, z north): facing +z, right is +x -> right = (fz, -fx).
  const right = (d: P2): P2 => ({ x: d.z, z: -d.x });
  const dot = (a: P2, b: P2) => a.x * b.x + a.z * b.z;
  const shifted = (p: P2, d: P2): P2 => ({ x: p.x + right(d).x * offset, z: p.z + right(d).z * offset });
  const points: P2[] = [shifted(centre[0]!, dirs[0]!)];
  const corners: P2[][] = [];
  for (let k = 1; k + 1 < centre.length; k++) {
    const d0 = dirs[k - 1]!;
    const d1 = dirs[k]!;
    const r0 = right(d0);
    const r1 = right(d1);
    // The lane's own corner: where the offset line before the corner meets the offset line after it.
    const before = shifted(centre[k]!, d0);
    const tau = (offset * (1 - dot(r0, r1))) / dot(d0, r1);
    const v = { x: before.x + d0.x * tau, z: before.z + d0.z * tau };
    const turnsRight = d0.x * d1.z - d0.z * d1.x < 0;
    const r = turnsRight ? STREET_CORNER_INSIDE_RADIUS : STREET_CORNER_OUTSIDE_RADIUS;
    const angle = Math.acos(Math.max(-1, Math.min(1, dot(d0, d1))));
    const back = r * Math.tan(angle / 2);
    const toCentre = turnsRight ? r0 : { x: -r0.x, z: -r0.z };
    const t0 = { x: v.x - d0.x * back, z: v.z - d0.z * back };
    const t1 = { x: v.x + d1.x * back, z: v.z + d1.z * back };
    const c = { x: t0.x + toCentre.x * r, z: t0.z + toCentre.z * r };
    const a0 = Math.atan2(t0.z - c.z, t0.x - c.x);
    let sweep = Math.atan2(t1.z - c.z, t1.x - c.x) - a0;
    while (sweep > Math.PI) sweep -= Math.PI * 2;
    while (sweep < -Math.PI) sweep += Math.PI * 2;
    const arcPoints: P2[] = [];
    for (let i = 0; i <= CORNER_STEPS; i++) {
      const a = a0 + (sweep * i) / CORNER_STEPS;
      arcPoints.push({ x: c.x + Math.cos(a) * r, z: c.z + Math.sin(a) * r });
    }
    points.push(...arcPoints);
    corners.push(arcPoints);
  }
  points.push(shifted(centre[centre.length - 1]!, dirs[dirs.length - 1]!));
  return { points, corners };
}

/**
 * A two-lane loop along an open polyline of streets joined at corners (world v4, ADR-0015: Willow
 * Avenue -> Market Street -> Aspen Avenue): the lane on the right of the forward direction from the
 * first point to the last with rounded corners, a U-turn at the last point, the other lane back and a
 * U-turn at the first point. The U-turns are slow zones as in laneLoop; the corners are `corners`
 * (slow too, and on the road all the way). With two points this is laneLoop's path.
 */
export function streetLoop(id: string, centre: readonly P2[], laneOffset: number): LoopPath {
  const outbound = offsetLane(centre, laneOffset);
  const back = offsetLane([...centre].reverse(), laneOffset);
  const a = centre[0]!;
  const a1 = centre[1]!;
  const b = centre[centre.length - 1]!;
  const b0 = centre[centre.length - 2]!;
  // angle(right) for a forward direction f is atan2(-fx, fz) (see laneLoop for the U-turn arcs).
  const angRight = (from: P2, to: P2) => Math.atan2(-(to.x - from.x), to.z - from.z);
  const turnB = arc(b.x, b.z, laneOffset, angRight(b0, b), angRight(b0, b) + Math.PI, 8);
  const turnA = arc(a.x, a.z, laneOffset, angRight(a, a1) + Math.PI, angRight(a, a1) + Math.PI * 2, 8);
  const outEnd = outbound.points[outbound.points.length - 1]!;
  const backStart = back.points[0]!;
  const backEnd = back.points[back.points.length - 1]!;
  const points = [...outbound.points, ...turnB, ...back.points, ...turnA];
  return makeLoop(id, points, [[outEnd, ...turnB, backStart], [backEnd, ...turnA]], [...outbound.corners, ...back.corners]);
}

export interface PathSample {
  x: number;
  z: number;
  /** Unit heading. */
  hx: number;
  hz: number;
}

export function wrapS(path: LoopPath, s: number): number {
  const r = s % path.length;
  return r < 0 ? r + path.length : r;
}

export function samplePath(path: LoopPath, s: number): PathSample {
  const t = wrapS(path, s);
  const n = path.points.length;
  // Binary search the segment.
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (path.cumulative[mid]! <= t) lo = mid;
    else hi = mid - 1;
  }
  const a = path.points[lo]!;
  const b = path.points[(lo + 1) % n]!;
  const segStart = path.cumulative[lo]!;
  const segLen = Math.max(1e-6, Math.hypot(b.x - a.x, b.z - a.z));
  const u = Math.min(1, Math.max(0, (t - segStart) / segLen));
  return { x: a.x + (b.x - a.x) * u, z: a.z + (b.z - a.z) * u, hx: (b.x - a.x) / segLen, hz: (b.z - a.z) / segLen };
}

export interface CarState {
  id: number;
  pathIndex: number;
  s: number;
  speed: number;
  /** Cruise speed (m/s) before gentle variation. */
  cruise: number;
  /** Phase for the slow speed variation. */
  phase: number;
  style: number;
  color: number;
  halfLength: number;
}

export interface TrafficOptions {
  /** Look-ahead for avatars on the lane (metres beyond the bumper). */
  yieldDistance: number;
  /** Half width of the lane corridor in which an avatar makes a car yield. */
  laneHalfWidth: number;
  /** Gap kept to the car ahead / an avatar when fully stopped (metres from the bumper). */
  stopGap: number;
  accel: number;
  brake: number;
  uTurnSpeed: number;
  /** A car waits (target speed 0) while a walker is within this distance of its driver's door. */
  doorWaitRadius: number;
}

export const DEFAULT_TRAFFIC: TrafficOptions = { yieldDistance: 7, laneHalfWidth: 1.7, stopGap: 1.8, accel: 2.6, brake: 9, uTurnSpeed: 3.2, doorWaitRadius: 2.0 };

/**
 * Cars per loop of the town's one street room, in trafficLoops(whole manifest) order (world v6, ADR-0042): Central's
 * v3 loops keep theirs, 10 on Main Street, 2 on each lane (Elm, Birch, Cedar, Maple) and 3 on Orchard Road (car ids
 * 0..20 exactly as world v3 seeded them); then 6 on Market Street between the avenues ("north"), 7 on the loop from
 * Aspen Avenue along Market Street into River Street ("north_east") and 7 on the one from Willow Avenue into High
 * Street ("north_west"); then Riverside's two lanes 2 each and Mill Road 3, Hillcrest's two lanes 2 each and Quarry
 * Road 3. 55 cars: as many as v5's three street rooms had together (Central 29, Riverside 13, Hillcrest 13), the v5
 * Willow / Market / Aspen loop's 8 and the two shopping streets' 6 + 6 now on the three north loops (20).
 * ADR-0044 (the Sports Park): then 4 on Stadium Way ("sportspark:main") and 2 on Arena Avenue ("sportspark:side1"), 61
 * cars; the first 55 keep their ids and starting places.
 */
export const TOWN_FLEET: readonly number[] = [10, 2, 2, 2, 2, 3, 6, 7, 7, 2, 2, 3, 2, 2, 3, 4, 2];

/**
 * Cars per loop on the server, in trafficLoops order. World v6 (ADR-0042): the server runs ONE street room for the
 * whole town on trafficLoops(WORLD_MANIFEST), so this is TOWN_FLEET (55 cars). History: v2 [6, 2, 2]; v3 (ADR-0008)
 * [10, 2, 2, 2, 2, 3]; v4 (ADR-0015) [10, 2, 2, 2, 2, 3, 8]; v5 that was Central's street room's fleet (the
 * per-district fleets are DISTRICT_FLEETS, districtSlice.ts).
 */
export const SERVER_FLEET: readonly number[] = TOWN_FLEET;

/** Position and heading of a traffic car on its loop (heading = yaw, 0 = +z). */
export function carPoseOnPath(car: Pick<CarState, "pathIndex" | "s">, paths: readonly LoopPath[]): VehiclePose {
  const p = samplePath(paths[car.pathIndex]!, car.s);
  return { x: p.x, z: p.z, heading: Math.atan2(p.hx, p.hz) };
}

/**
 * Gap from the car's front bumper to the nearest part of a blocker (a driven or parked car) that
 * reaches into its lane corridor ahead, or Infinity.
 */
function blockerGap(car: CarState, p: PathSample, blockers: readonly Obb[], opts: TrafficOptions): number {
  let best = Infinity;
  for (const o of blockers) {
    let minAlong = Infinity;
    let maxAlong = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    for (const c of obbCorners(o)) {
      const dx = c.x - p.x;
      const dz = c.z - p.z;
      const along = dx * p.hx + dz * p.hz;
      const lat = -dx * p.hz + dz * p.hx;
      minAlong = Math.min(minAlong, along);
      maxAlong = Math.max(maxAlong, along);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    }
    if (maxLat < -opts.laneHalfWidth || minLat > opts.laneHalfWidth) continue; // not in this lane
    if (maxAlong <= -0.2) continue; // behind
    const gap = Math.max(0, minAlong - car.halfLength);
    if (gap < best) best = gap;
  }
  return best;
}

/** Gap from the car's front bumper to the nearest avatar in its lane corridor within the look-ahead, or Infinity. */
function actorGap(car: CarState, p: PathSample, actors: readonly P2[], opts: TrafficOptions): number {
  let best = Infinity;
  for (const a of actors) {
    const dx = a.x - p.x;
    const dz = a.z - p.z;
    const along = dx * p.hx + dz * p.hz;
    const lateral = Math.abs(-dx * p.hz + dz * p.hx);
    if (along <= -0.2 || lateral > opts.laneHalfWidth) continue;
    const gap = along - car.halfLength;
    if (gap < opts.yieldDistance && gap < best) best = Math.max(0, gap);
  }
  return best;
}

/** The next car ahead on the same loop (`index` into `cars`, -1 if alone) and the bumper-to-bumper gap to it. */
function carAhead(car: CarState, cars: readonly CarState[], path: LoopPath): { index: number; gap: number } {
  let best = Infinity;
  let index = -1;
  cars.forEach((o, k) => {
    if (o === car || o.pathIndex !== car.pathIndex) return;
    const ahead = wrapS(path, o.s - car.s);
    if (ahead <= 0) return;
    const gap = ahead - car.halfLength - o.halfLength;
    if (gap < best) {
      best = Math.max(0, gap);
      index = k;
    }
  });
  return { index, gap: best };
}

/**
 * Distance from the car's front bumper to the nearest obstacle ahead in its lane (avatars, the car
 * in front on the same loop, and blockers = driven/parked cars), or Infinity. Junction yielding is
 * separate (junctionGaps); stepTraffic applies both.
 */
export function obstacleAhead(
  car: CarState,
  cars: readonly CarState[],
  paths: readonly LoopPath[],
  actors: readonly P2[],
  opts: TrafficOptions = DEFAULT_TRAFFIC,
  blockers: readonly Obb[] = [],
): number {
  const path = paths[car.pathIndex]!;
  const p = samplePath(path, car.s);
  let best = Math.min(actorGap(car, p, actors, opts), carAhead(car, cars, path).gap);
  if (blockers.length) best = Math.min(best, blockerGap(car, p, blockers, opts));
  return best;
}

/** True while a walker stands within `doorWaitRadius` of the car's driver's door (it waits for them). */
export function someoneAtDriverDoor(car: CarState, paths: readonly LoopPath[], actors: readonly P2[], opts: TrafficOptions = DEFAULT_TRAFFIC): boolean {
  if (!actors.length) return false;
  const door = driverDoorPoint(car.style, carPoseOnPath(car, paths));
  const r2 = opts.doorWaitRadius * opts.doorWaitRadius;
  return actors.some((a) => {
    const dx = a.x - door.x;
    const dz = a.z - door.z;
    return dx * dx + dz * dz <= r2;
  });
}

/** True inside a U-turn or (world v4) a rounded corner: the car keeps to uTurnSpeed and never respawns there. */
function inSlowZone(path: LoopPath, s: number): boolean {
  const t = wrapS(path, s);
  const inside = ([a, b]: [number, number]) => (t >= a && t <= b) || (t + path.length >= a && t + path.length <= b) || (t - path.length >= a && t - path.length <= b);
  return path.slowZones.some(inside) || (path.corners?.some(inside) ?? false);
}

/** Target speed given the obstacle gap: full cruise beyond the look-ahead, 0 at the stop gap. */
export function targetSpeed(cruise: number, gap: number, opts: TrafficOptions = DEFAULT_TRAFFIC): number {
  if (!Number.isFinite(gap) || gap >= opts.yieldDistance) return cruise;
  if (gap <= opts.stopGap) return 0;
  const v = cruise * ((gap - opts.stopGap) / (opts.yieldDistance - opts.stopGap));
  // Creeping at walking pace toward a pedestrian looks wrong: below ~1 km/h just stop.
  return v < 0.3 ? 0 : v;
}

// ---------------------------------------------------------------------------------------------
// Junctions (world v3)
// ---------------------------------------------------------------------------------------------

/** A car this close (centre distance to a span start, metres) takes part in the junction's turn order. */
export const JUNCTION_LOOKAHEAD = 20;
/** A yielding car stops this much further back than strictly needed (cosmetic stop line). */
export const JUNCTION_STOP_BACK = 2;
/** Extra room a car needs beyond a junction before it may enter it ("don't block the box"). */
export const JUNCTION_EXIT_CLEARANCE = 0.5;
/** Junction geometry: sample step along each loop and safety margin added around every car body. */
const JUNCTION_SAMPLE = 0.25;
const JUNCTION_MARGIN = 0.3;
/**
 * Two places on ONE loop at least this far apart along it whose car bodies could touch make a
 * junction of that loop with itself (world v4 street-loop corners). Closer places are the same car's
 * neighbourhood (a body is 5.6 m long with the margin; a U-turn joins the two lanes within ~15 m).
 */
const SELF_JUNCTION_MIN_ARC = 30;

/** True when arc position `s` lies in the span (centre inside [start, end], wrapping). */
function inSpan(path: LoopPath, span: JunctionSpan, s: number): boolean {
  return wrapS(path, s - span.start) <= span.end - span.start;
}

/**
 * Junction zones of a set of loops: every place where a car body on one loop could touch a car body
 * on ANOTHER loop (largest body of VEHICLE_SPECS plus a 0.3 m margin, sampled every 0.25 m), merged
 * into zones. Returns copies of the loops with `junctions` set (sorted by start). Loops of one road
 * never conflict with themselves (their two directions are parallel lanes) except where a loop turns
 * a corner (world v4 streetLoop): places on one loop at least SELF_JUNCTION_MIN_ARC apart whose bodies
 * could touch are a junction too, and that loop's spans each get their own turn-taking `group`.
 */
export function withJunctions(loops: readonly LoopPath[]): LoopPath[] {
  const H = Math.max(...VEHICLE_SPECS.map((s) => s.halfLength)) + JUNCTION_MARGIN;
  const W = Math.max(...VEHICLE_SPECS.map((s) => s.halfWidth)) + JUNCTION_MARGIN;
  const reach = 2 * Math.hypot(H, W);
  const CELL = 8;
  const key = (ix: number, iz: number) => (ix + 4096) * 8192 + (iz + 4096);
  const samples = loops.map((loop) => {
    const n = Math.max(1, Math.ceil(loop.length / JUNCTION_SAMPLE));
    const step = loop.length / n;
    const out: { s: number; box: Obb }[] = [];
    const grid = new Map<number, number[]>();
    for (let k = 0; k < n; k++) {
      const s = k * step;
      const p = samplePath(loop, s);
      const box: Obb = { cx: p.x, cz: p.z, halfWidth: W, halfLength: H, heading: Math.atan2(p.hx, p.hz) };
      out.push({ s, box });
      const g = key(Math.floor(p.x / CELL), Math.floor(p.z / CELL));
      const list = grid.get(g);
      if (list) list.push(k);
      else grid.set(g, [k]);
    }
    return { step, out, grid, flags: new Uint8Array(n) };
  });
  const pairs: [number, number, number, number][] = [];
  const selfLoops = new Set<number>();
  for (let i = 0; i < loops.length; i++) {
    for (let j = i; j < loops.length; j++) {
      const A = samples[i]!;
      const B = samples[j]!;
      const L = loops[i]!.length;
      A.out.forEach((a, ka) => {
        const x0 = Math.floor((a.box.cx - reach) / CELL);
        const x1 = Math.floor((a.box.cx + reach) / CELL);
        const z0 = Math.floor((a.box.cz - reach) / CELL);
        const z1 = Math.floor((a.box.cz + reach) / CELL);
        for (let gx = x0; gx <= x1; gx++) {
          for (let gz = z0; gz <= z1; gz++) {
            for (const kb of B.grid.get(key(gx, gz)) ?? []) {
              if (i === j) {
                // The same loop: each pair once, and only places far apart along it.
                if (kb <= ka) continue;
                const along = Math.abs(B.out[kb]!.s - a.s);
                if (Math.min(along, L - along) < SELF_JUNCTION_MIN_ARC) continue;
              }
              const b = B.out[kb]!;
              if (Math.abs(a.box.cx - b.box.cx) >= reach || Math.abs(a.box.cz - b.box.cz) >= reach) continue;
              if (!obbIntersectsObb(a.box, b.box)) continue;
              A.flags[ka] = 1;
              B.flags[kb] = 1;
              pairs.push([i, ka, j, kb]);
              if (i === j) selfLoops.add(i);
            }
          }
        }
      });
    }
  }
  // Runs of flagged samples -> spans (a run through sample 0 joins the run at the loop's end).
  const spanOf = samples.map((x) => new Int32Array(x.out.length).fill(-1));
  const spans: { loop: number; start: number; end: number }[] = [];
  samples.forEach((smp, li) => {
    const n = smp.out.length;
    const f = smp.flags;
    if (!f.some((v) => v === 1)) return;
    if (f.every((v) => v === 1)) {
      const id = spans.push({ loop: li, start: 0, end: loops[li]!.length }) - 1;
      spanOf[li]!.fill(id);
      return;
    }
    // Start scanning just after an unflagged sample so no run is split by the wrap.
    let first = 0;
    while (f[first] === 1) first++;
    let k = 0;
    while (k < n) {
      const idx = (first + k) % n;
      if (f[idx] !== 1) {
        k++;
        continue;
      }
      let len = 0;
      while (len < n && f[(first + k + len) % n] === 1) len++;
      const startIdx = idx;
      const L = loops[li]!.length;
      let start = smp.out[startIdx]!.s - smp.step;
      if (start < 0) start += L;
      const end = start + (len + 1) * smp.step;
      const id = spans.push({ loop: li, start, end }) - 1;
      for (let t = 0; t < len; t++) spanOf[li]![(startIdx + t) % n] = id;
      k += len;
    }
  });
  // Union-find: spans whose samples conflict share a zone.
  const parent = spans.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]!]!;
      i = parent[i]!;
    }
    return i;
  };
  for (const [i, ka, j, kb] of pairs) {
    const a = find(spanOf[i]![ka]!);
    const b = find(spanOf[j]![kb]!);
    if (a !== b) parent[Math.max(a, b)] = Math.min(a, b);
  }
  const zoneIds = new Map<number, number>();
  const zoneOf = (spanIndex: number): number => {
    const root = find(spanIndex);
    let z = zoneIds.get(root);
    if (z === undefined) {
      z = zoneIds.size;
      zoneIds.set(root, z);
    }
    return z;
  };
  return loops.map((loop, li) => {
    const own = spans
      .map((sp, i) => ({ sp, i }))
      .filter(({ sp }) => sp.loop === li)
      .map(({ sp, i }): JunctionSpan => (selfLoops.has(li) ? { zone: zoneOf(i), start: sp.start, end: sp.end, group: loops.length + i } : { zone: zoneOf(i), start: sp.start, end: sp.end }))
      .sort((a, b) => a.start - b.start);
    return { ...loop, junctions: own };
  });
}

/** The turn-taking group of a span on loop `pathIndex` (JunctionSpan.group). */
export function spanGroup(span: JunctionSpan, pathIndex: number): number {
  return span.group ?? pathIndex;
}

/**
 * Room ahead of `car` on its loop before the rear of the next car on the same loop (Infinity if alone), and that
 * car's index in `cars` (-1 if alone).
 */
function roomAhead(car: CarState, cars: readonly CarState[], path: LoopPath): { room: number; index: number } {
  let best = Infinity;
  let index = -1;
  cars.forEach((o, k) => {
    if (o === car || o.pathIndex !== car.pathIndex) return;
    const ahead = wrapS(path, o.s - car.s);
    if (ahead <= 0) return;
    if (ahead - o.halfLength < best) {
      best = ahead - o.halfLength;
      index = k;
    }
  });
  return { room: best, index };
}

/** Sample step (metres of arc) of the junction blocker check (blockedOnTheWay). */
const BLOCKER_CHECK_STEP = 0.5;

/**
 * ADR-0045: true when a driven or parked car (a blocker) would stop `car` before its centre reaches arc position `to`
 * on its loop: at some centre position on the way (every 0.5 m, and at `to`), a blocker reaches into its lane corridor
 * within `stopGap` of its front bumper. That is the test obstacleAhead applies while the car drives (blockerGap), at
 * each pose along its path, so it follows a junction's curve too. Blockers farther than they could ever reach the
 * corridor on the way are skipped (centre-distance prefilter).
 */
function blockedOnTheWay(car: CarState, path: LoopPath, to: number, blockers: readonly Obb[], opts: TrafficOptions): boolean {
  const dist = wrapS(path, to - car.s);
  const from = samplePath(path, car.s);
  // A blocker blockerGap counts within stopGap overlaps the box `halfLength + stopGap` ahead x `laneHalfWidth` either
  // side in its projections, so its centre is within hypot(halfLength + stopGap, laneHalfWidth) + sqrt(2) x its half
  // diagonal of the car's centre there; every centre on the way is within `dist` (arc >= chord) of the car's centre now.
  const corridor = Math.hypot(car.halfLength + opts.stopGap, opts.laneHalfWidth);
  const near = blockers.filter((o) => Math.hypot(o.cx - from.x, o.cz - from.z) <= dist + corridor + 1.5 * Math.hypot(o.halfLength, o.halfWidth));
  if (near.length === 0) return false;
  for (let u = 0; ; u = Math.min(dist, u + BLOCKER_CHECK_STEP)) {
    if (blockerGap(car, samplePath(path, car.s + u), near, opts) <= opts.stopGap) return true;
    if (u >= dist) return false;
  }
}

/** Why a car waits at a junction (one entry per junction zone it yields at this step). */
interface JunctionWait {
  /** Front-bumper gap to its stop point for this junction (JUNCTION_STOP_BACK before the span). */
  gap: number;
  /** ADR-0045: a driven or parked car keeps it out (it could not leave the junction: "don't block the box"). */
  blocker: boolean;
  /**
   * Indices (into `cars`) of the cars it waits for: the car ahead on its loop that leaves no room beyond the junction,
   * or, when it could go, the cars of another turn-taking group inside the junction, or the car whose turn it is.
   */
  cars: number[];
}

/** junctionGaps and, per car, why it waits (trafficHolds). */
function junctionTurns(
  cars: readonly CarState[],
  paths: readonly LoopPath[],
  opts: TrafficOptions,
  blockers: readonly Obb[],
): { gaps: number[]; waits: JunctionWait[][] } {
  const gaps = cars.map(() => Infinity);
  const waits: JunctionWait[][] = cars.map(() => []);
  type Approach = { car: number; d: number; ready: boolean; group: number; roomCar: number; blocker: boolean };
  const zones = new Map<number, { inside: Set<number>; insideCars: { car: number; group: number }[]; approach: Approach[] }>();
  const zoneState = (z: number) => {
    let st = zones.get(z);
    if (!st) {
      st = { inside: new Set<number>(), insideCars: [], approach: [] };
      zones.set(z, st);
    }
    return st;
  };
  cars.forEach((car, k) => {
    const path = paths[car.pathIndex];
    const spans = path?.junctions;
    if (!path || !spans || spans.length === 0) return;
    for (const span of spans) {
      const group = spanGroup(span, car.pathIndex);
      if (inSpan(path, span, car.s)) {
        const st = zoneState(span.zone);
        st.inside.add(group);
        st.insideCars.push({ car: k, group });
        continue;
      }
      const d = wrapS(path, span.start - car.s);
      if (d > JUNCTION_LOOKAHEAD) continue;
      const needed = d + (span.end - span.start) + JUNCTION_EXIT_CLEARANCE + car.halfLength + opts.stopGap;
      const room = roomAhead(car, cars, path);
      const roomy = room.room >= needed;
      // ADR-0045: "don't block the box" counts driven and parked cars too: a car does not enter a junction where one
      // would stop it before its centre is JUNCTION_EXIT_CLEARANCE past the span (it would stand inside the junction,
      // the crossing road waiting for it). Without blockers this is v3's rule exactly.
      const blocked = roomy && blockers.length > 0 && blockedOnTheWay(car, path, span.end + JUNCTION_EXIT_CLEARANCE, blockers, opts);
      zoneState(span.zone).approach.push({ car: k, d, ready: roomy && !blocked, group, roomCar: roomy ? -1 : room.index, blocker: blocked });
    }
  });
  for (const st of zones.values()) {
    if (st.approach.length === 0) continue;
    let allowed: number | null = null;
    let win: Approach | null = null;
    if (st.inside.size === 1) allowed = [...st.inside][0]!;
    else if (st.inside.size === 0) {
      for (const a of st.approach) {
        if (!a.ready) continue;
        if (!win) {
          win = a;
          continue;
        }
        const ca = cars[a.car]!;
        const cw = cars[win.car]!;
        if (a.d < win.d - 1e-9 || (Math.abs(a.d - win.d) <= 1e-9 && (a.group < win.group || (a.group === win.group && ca.id < cw.id)))) win = a;
      }
      allowed = win ? win.group : null;
    }
    for (const a of st.approach) {
      if (a.ready && allowed !== null && a.group === allowed) continue;
      const gap = Math.max(0, a.d - JUNCTION_STOP_BACK);
      gaps[a.car] = Math.min(gaps[a.car]!, gap);
      const waitFor = !a.ready
        ? a.roomCar >= 0
          ? [a.roomCar]
          : []
        : st.inside.size > 0
          ? st.insideCars.filter((c) => c.group !== a.group || allowed === null).map((c) => c.car)
          : win
            ? [win.car]
            : [];
      waits[a.car]!.push({ gap, blocker: a.blocker, cars: waitFor });
    }
  }
  return { gaps, waits };
}

/**
 * Junction turn order for one step (world v3): for each car, the front-bumper gap to the point where
 * it must stop because it may not enter a junction yet, or Infinity. Rules, per junction zone:
 *  - a car whose centre is inside a span is IN the junction and never yields to the junction rule
 *    (only to walkers and the car ahead);
 *  - while any car of one loop is in the junction, cars of OTHER loops wait before it; cars of the
 *    same loop (the other direction, or one following) may enter;
 *  - when it is empty, the approaching car nearest its span (ties: lower loop index, then lower id)
 *    goes, with the other approaching cars of its loop; everyone else waits;
 *  - a car only enters when there is room for it beyond the junction ("don't block the box"), so a
 *    car in a junction can always leave it. Together these keep two loops from ever being in one
 *    junction at once and rule out deadlocks between junctions (traffic.test.ts long simulations).
 *    ADR-0045: `blockers` (driven and parked cars) count too: a car does not enter while one stands
 *    where it would stop it before it is out of the junction (blockedOnTheWay). With no blockers the
 *    result is exactly v3's.
 * "Loop" above means the span's turn-taking group (spanGroup): the loop itself, except at the corners
 * of a world v4 street loop, where its two directions take turns like two loops.
 */
export function junctionGaps(cars: readonly CarState[], paths: readonly LoopPath[], opts: TrafficOptions = DEFAULT_TRAFFIC, blockers: readonly Obb[] = []): number[] {
  return junctionTurns(cars, paths, opts, blockers).gaps;
}

/** What holds a stopped traffic car this step (trafficHolds). */
export interface TrafficHold {
  /** A walker holds it: in its lane within the stop distance, or at its driver's door. */
  walker: boolean;
  /**
   * A driven or parked car holds it: in its lane within the stop distance, or (ADR-0045) keeping it out of the
   * junction it waits at.
   */
  blocker: boolean;
  /**
   * Indices (into `cars`) of the traffic cars it waits for: the car ahead on its loop within the stop distance, and at
   * the junction it waits at the cars it waits its turn for (see JunctionWait).
   */
  cars: number[];
}

/**
 * ADR-0045: what holds each car (positions after a step; used for stopped cars): its direct holds (walker, driven or
 * parked car) and the traffic cars it waits for. An obstacle (walker, car, blocker, junction stop point) within
 * stopGap + 0.5 m of the front bumper holds it (the server's v3 "held by a car" distance). Pure: the server's give-way
 * rules read it.
 */
export function trafficHolds(
  cars: readonly CarState[],
  paths: readonly LoopPath[],
  actors: readonly P2[],
  opts: TrafficOptions = DEFAULT_TRAFFIC,
  blockers: readonly Obb[] = [],
): TrafficHold[] {
  const near = opts.stopGap + 0.5;
  const turns = junctionTurns(cars, paths, opts, blockers);
  return cars.map((car, i) => {
    const path = paths[car.pathIndex]!;
    const p = samplePath(path, car.s);
    const walker = actorGap(car, p, actors, opts) <= near || someoneAtDriverDoor(car, paths, actors, opts);
    let blocker = blockers.length > 0 && blockerGap(car, p, blockers, opts) <= near;
    const waitFor: number[] = [];
    const ahead = carAhead(car, cars, path);
    if (ahead.index >= 0 && ahead.gap <= near) waitFor.push(ahead.index);
    for (const w of turns.waits[i]!) {
      if (w.gap > near) continue;
      if (w.blocker) blocker = true;
      waitFor.push(...w.cars);
    }
    return { walker, blocker, cars: waitFor };
  });
}

/**
 * ADR-0045: the stopped cars whose wait reaches a driven or parked car: held by one directly, or waiting for a stopped
 * car (the car ahead, or at a junction the cars it waits its turn for) whose wait reaches one, through any chain or
 * cycle of such waits. A car a walker holds is never one, and no chain passes through a car a walker holds or a car
 * still moving. A pure-traffic cycle does not count (the junction rules rule them out).
 */
export function waitsOnBlockers(cars: readonly CarState[], holds: readonly TrafficHold[]): boolean[] {
  const eligible = cars.map((c, i) => c.speed === 0 && !holds[i]!.walker);
  const on = holds.map((h, i) => eligible[i]! && h.blocker);
  for (let changed = true; changed; ) {
    changed = false;
    holds.forEach((h, i) => {
      if (on[i] || !eligible[i] || !h.cars.some((k) => on[k])) return;
      on[i] = true;
      changed = true;
    });
  }
  return on;
}

/**
 * Advance every car by dt seconds (mutates `cars`). `timeSeconds` drives the gentle speed variation.
 * `blockers` are the boxes of driven and parked cars: a traffic car treats one in its lane corridor
 * like the car ahead, and (ADR-0045) does not enter a junction where one would stop it inside. A car
 * waits (target speed 0) while any walker is within `doorWaitRadius` of its driver's door, and takes
 * its turn at junctions (junctionGaps).
 */
export function stepTraffic(
  cars: CarState[],
  paths: readonly LoopPath[],
  actors: readonly P2[],
  dt: number,
  timeSeconds: number,
  opts: TrafficOptions = DEFAULT_TRAFFIC,
  blockers: readonly Obb[] = [],
): void {
  const step = Math.min(Math.max(dt, 0), 0.1);
  if (step === 0) return;
  const junction = junctionGaps(cars, paths, opts, blockers);
  const gaps = cars.map((c, i) => Math.min(obstacleAhead(c, cars, paths, actors, opts, blockers), junction[i]!));
  const waiting = cars.map((c) => someoneAtDriverDoor(c, paths, actors, opts));
  cars.forEach((car, i) => {
    const path = paths[car.pathIndex]!;
    let cruise = car.cruise * (1 + 0.12 * Math.sin(timeSeconds * 0.23 + car.phase));
    if (inSlowZone(path, car.s)) cruise = Math.min(cruise, opts.uTurnSpeed);
    const gap = gaps[i]!;
    const want = waiting[i] ? 0 : targetSpeed(cruise, gap, opts);
    const dv = want - car.speed;
    car.speed = Math.max(0, car.speed + (dv > 0 ? Math.min(dv, opts.accel * step) : Math.max(dv, -opts.brake * step)));
    if (want === 0 && car.speed < 0.6) car.speed = 0;
    // Never drive into an obstacle that appeared right in front (e.g. an avatar stepping out).
    if (gap <= opts.stopGap * 0.5) car.speed = 0;
    let advance = car.speed * step;
    if (Number.isFinite(gap) && advance > Math.max(0, gap - opts.stopGap * 0.5)) {
      // Held back by what is ahead: the speed is what the car actually moves (a car waiting behind a walker in its lane
      // reports 0, not a crawl it cannot do).
      advance = Math.max(0, gap - opts.stopGap * 0.5);
      car.speed = advance / step;
    }
    car.s = wrapS(path, car.s + advance);
  });
}

/** Spread `count` cars over a loop with deterministic jitter. */
export function seedCars(pathIndex: number, path: LoopPath, count: number, startId: number, seed: number, styles: number, colors: number): CarState[] {
  const out: CarState[] = [];
  for (let i = 0; i < count; i++) {
    const h = fract(Math.sin((seed + i * 7.31) * 12.9898) * 43758.5453);
    const h2 = fract(Math.sin((seed + i * 3.17) * 78.233) * 12543.123);
    const style = (startId + i) % Math.max(1, styles);
    out.push({
      id: startId + i,
      pathIndex,
      s: wrapS(path, (path.length * i) / Math.max(1, count) + h * 9),
      speed: 0,
      cruise: 6.2 + h2 * 2.4,
      phase: h * Math.PI * 2,
      style,
      color: Math.floor(h2 * 997 + i * 3) % colors,
      halfLength: vehicleSpec(style).halfLength,
    });
  }
  return out;
}

function fract(v: number): number {
  return v - Math.floor(v);
}

/** Main Street U-turns are centred this far in from the street ends (round 2 used 4 m; at 6 m a van's swing clears the end lamps). */
const MAIN_UTURN_INSET = 6;
/** A loop ending at a crossing road U-turns this far before that road (the lanes at Main Street since round 2). */
const CROSSING_UTURN_SETBACK = 4.5;
/** A loop ending at a dead end U-turns this far in from the road end. */
const DEAD_END_UTURN_INSET = 4;

const EPS = 1e-6;

/** North-south road pieces of one lane (same x extent) merged into one column. */
function mergeColumns(rects: readonly Aabb[]): Aabb[] {
  const out: Aabb[] = [];
  for (const r of rects) {
    const same = out.find((o) => Math.abs(o.minX - r.minX) < EPS && Math.abs(o.maxX - r.maxX) < EPS);
    if (same) {
      same.minZ = Math.min(same.minZ, r.minZ);
      same.maxZ = Math.max(same.maxZ, r.maxZ);
    } else out.push({ ...r });
  }
  return out;
}

/**
 * The lane loops of a manifest's roads, with junction zones (withJunctions). For ONE district's roads (a district
 * slice, or a one-district world):
 *  - "main": Main Street (the longest east-west road), U-turns 6 m before each end;
 *  - "side1".."sideN": each north-south lane (pieces split by a crossing road merged), inner lanes
 *    first (|x| ascending, then west before east: Elm, Birch, Cedar, Maple in v3, so the v2 lanes
 *    keep loop indices 1 and 2), U-turn 4.5 m south of Main Street and 4 m before the lane's end;
 *  - "back" (or "back1".. if there are several): each other east-west road, U-turning 4.5 m before
 *    a lane it ends at (Orchard Road at Cedar and Maple), else 4 m before its end;
 *  - world v4 (ADR-0015), the roads NORTH of Main Street: "north" (or "north1"..), one loop per
 *    east-west street joined to Main Street by an avenue at each end (Willow Avenue -> Market Street ->
 *    Aspen Avenue, streetLoop), U-turning 4.5 m north of Main Street at both avenue mouths; any other
 *    road north of Main Street gets a plain lane loop; world v6 (ADR-0042): a street that goes on past its
 *    avenue corners (T-junctions) is driven by three loops instead, see northLoops.
 * The v3 loops keep their order and ids; the north loops come after them.
 *
 * World v6 (ADR-0042, one continuous town): handed the WHOLE manifest of a town of several districts, the loops of
 * the whole town (townLoops): cars drive between the districts. Central's first six loops (Main Street, the four
 * lanes, Orchard Road) are exactly v5's; then "north" (Market Street between Willow and Aspen Avenue), "north_east"
 * (up Aspen Avenue, right along Market Street into River Street to its far end and back), "north_west" (the same by
 * Willow Avenue into High Street), then each other district's lanes and back road ("riverside:side1", ...); ADR-0044:
 * then the Sports Park's, "sportspark:main" (Stadium Way) and "sportspark:side1" (Arena Avenue, from 4.5 m south of
 * Stadium Way down to 4 m north of Market Street's kerb, through both its stretches). Its fleet is TOWN_FLEET
 * (= SERVER_FLEET). Handed a district slice, that district's own loops (districtManifest(m,
 * id).trafficLoops caches them; v5 behaviour for Riverside and Hillcrest, whose street now starts at the old wall line;
 * Central's slice has the three north loops ending at its edge). v5: the whole manifest meant Central.
 */
export function trafficLoops(manifest: WorldManifest): LoopPath[] {
  if (manifest.districts.length > 1) return withJunctions(townLoops(manifest));
  return withJunctions(roadLoops(roadRects(manifest)));
}

function roadRects(m: WorldManifest): Aabb[] {
  return m.surfaces.filter((s) => s.kind === "road").map((s) => s.aabb);
}

const isEastWest = (r: Aabb) => r.maxX - r.minX >= r.maxZ - r.minZ;

/** The longest east-west road (a district's main street). */
function mainRoad(roads: readonly Aabb[]): Aabb {
  return roads.reduce((a, b) => (b.maxX - b.minX > a.maxX - a.minX ? b : a), roads[0]!);
}

/** True when `b` carries on the east-west road `a` (the same z band, end to end). */
function carriesOn(a: Aabb, b: Aabb): boolean {
  return Math.abs(a.minZ - b.minZ) < EPS && Math.abs(a.maxZ - b.maxZ) < EPS && (Math.abs(a.maxX - b.minX) < EPS || Math.abs(b.maxX - a.minX) < EPS);
}

/** The loops of one road network (see trafficLoops), without junction zones. `main` defaults to the longest east-west road. */
function roadLoops(roads: readonly Aabb[], main: Aabb = mainRoad(roads)): LoopPath[] {
  const mainZ = (main.minZ + main.maxZ) / 2;
  const lane = (main.maxZ - main.minZ) / 4;
  const loops: LoopPath[] = [laneLoop("main", { x: main.minX + MAIN_UTURN_INSET, z: mainZ }, { x: main.maxX - MAIN_UTURN_INSET, z: mainZ }, lane)];
  const cx = (r: Aabb) => (r.minX + r.maxX) / 2;
  const isNorth = (r: Aabb) => r !== main && r.minZ >= main.maxZ - EPS;
  const south = roads.filter((r) => r !== main && !isNorth(r));
  const sides = mergeColumns(south.filter((r) => r.maxZ - r.minZ > r.maxX - r.minX)).sort((a, b) => Math.abs(cx(a)) - Math.abs(cx(b)) || cx(a) - cx(b));
  for (const r of sides) {
    const x = cx(r);
    const l = (r.maxX - r.minX) / 4;
    loops.push(laneLoop(`side${loops.length}`, { x, z: main.minZ - CROSSING_UTURN_SETBACK }, { x, z: r.minZ + DEAD_END_UTURN_INSET }, l));
  }
  const backs = south.filter(isEastWest).sort((a, b) => b.maxZ - a.maxZ || a.minX - b.minX);
  backs.forEach((r, k) => {
    const z = (r.minZ + r.maxZ) / 2;
    const l = (r.maxZ - r.minZ) / 4;
    const covers = (v: Aabb, x: number) => v.minX <= x + EPS && v.maxX >= x - EPS && v.minZ <= r.minZ + EPS && v.maxZ >= r.maxZ - EPS;
    const west = sides.find((v) => covers(v, r.minX));
    const east = sides.find((v) => covers(v, r.maxX));
    const ax = west ? west.maxX + CROSSING_UTURN_SETBACK : r.minX + DEAD_END_UTURN_INSET;
    const bx = east ? east.minX - CROSSING_UTURN_SETBACK : r.maxX - DEAD_END_UTURN_INSET;
    loops.push(laneLoop(backs.length === 1 ? "back" : `back${k + 1}`, { x: ax, z }, { x: bx, z }, l));
  });
  loops.push(...northLoops(roads.filter(isNorth), main));
  return loops;
}

/**
 * World v6 (ADR-0042): the loops of a town of several districts joined by road (trafficLoops of the whole manifest).
 * The first district (Central) gets its loops over its own roads PLUS the other districts' roads that carry on one of
 * its streets north of its main street, end to end (River Street and High Street carry on Market Street at z = 70),
 * so its north loops drive on into those districts (northLoops). Every other district then gets its own loops
 * without the one on such a road (its "main" loop on its shopping street), their ids prefixed `<districtId>:`. Order:
 * the first district's loops, then each other district's, in manifest order.
 */
function townLoops(m: WorldManifest): LoopPath[] {
  const [first, ...others] = m.districts.map((d) => ({ id: d.id, roads: roadRects(districtItems(m, d.id)) }));
  const main = mainRoad(first!.roads);
  const chain = first!.roads.filter((r) => isEastWest(r) && r !== main && r.minZ >= main.maxZ - EPS);
  const carried: Aabb[] = [];
  const candidates = others.flatMap((o) => o.roads.filter(isEastWest));
  for (let grew = true; grew; ) {
    grew = false;
    for (const r of candidates) {
      if (carried.includes(r) || !chain.some((c) => carriesOn(c, r))) continue;
      carried.push(r);
      chain.push(r);
      grew = true;
    }
  }
  // ADR-0044: the first district's north-south roads that another district's north-south road carries on end to end
  // (Arena Avenue's stretch in Central, which the Sports Park's stretch continues) are driven with THAT district's
  // roads: one column, one loop, from the district's street down to the first district's street.
  const handed = new Map<string, Aabb[]>();
  const handedAll = new Set<Aabb>();
  for (const o of others) {
    const net = [...o.roads];
    const mine: Aabb[] = [];
    for (let grew = true; grew; ) {
      grew = false;
      for (const r of first!.roads) {
        if (isEastWest(r) || handedAll.has(r) || !net.some((b) => !isEastWest(b) && carriesOnNorthSouth(r, b))) continue;
        mine.push(r);
        net.push(r);
        handedAll.add(r);
        grew = true;
      }
    }
    handed.set(o.id, mine);
  }
  const loops = roadLoops([...first!.roads.filter((r) => !handedAll.has(r)), ...carried], main);
  for (const o of others) {
    if (!o.roads.length) continue; // a district without roads has no traffic of its own
    const ownMain = mainRoad(o.roads);
    for (const loop of roadLoops([...o.roads, ...(handed.get(o.id) ?? [])], ownMain)) {
      if (loop.id === "main" && carried.includes(ownMain)) continue; // driven by the first district's north loops
      loops.push({ ...loop, id: `${o.id}:${loop.id}` });
    }
  }
  return loops;
}

/** True when the north-south road `b` carries on `a` (the same x band, end to end). */
function carriesOnNorthSouth(a: Aabb, b: Aabb): boolean {
  return Math.abs(a.minX - b.minX) < EPS && Math.abs(a.maxX - b.maxX) < EPS && (Math.abs(a.maxZ - b.minZ) < EPS || Math.abs(b.maxZ - a.minZ) < EPS);
}

/** East-west road rectangles that carry on one another end to end (world v6: Market Street, River Street, High Street) as one street. */
function streetChains(rects: readonly Aabb[]): Aabb[] {
  const out: Aabb[] = [];
  for (const r of [...rects].sort((a, b) => a.minZ - b.minZ || a.minX - b.minX)) {
    const last = out.find((c) => carriesOn(c, r) && Math.abs(c.maxX - r.minX) < EPS);
    if (last) last.maxX = r.maxX;
    else out.push({ ...r });
  }
  return out;
}

/**
 * World v4 loops north of Main Street: an east-west street whose two ends sit on top of an avenue
 * each (the avenue's road ends where the street's begins, inside the street's x range) gives one
 * streetLoop up the west avenue, along the street and down the east avenue, U-turning 4.5 m north of
 * Main Street at both mouths. Roads not used that way get lane loops (U-turn 4.5 m from Main Street
 * and 4 m before a dead end).
 *
 * World v6 (ADR-0042): road rectangles that carry on one another end to end are one street (streetChains), and a
 * street that goes on past an avenue's corner makes that corner a T-junction. Such a street gets, instead of the one
 * v4 loop:
 *  - the street between the two avenues ("north"): a lane loop U-turning 4.5 m before each T-junction (the T rule of
 *    the lanes at Main Street and of Orchard Road at Cedar / Maple Lane), or, at an end that is still a corner, going
 *    round it into that avenue as in v4;
 *  - for each side the street goes on to ("north_east", "north_west"): a street loop up that avenue from 4.5 m north of
 *    Main Street, round the corner onto the street and along it to 6 m before its far end (Main Street's U-turn inset;
 *    the far ends have billboards like Main Street's), and back. Its two directions meet at the corner, where they take
 *    turns as the v4 corners did (withJunctions: a self-junction, one turn-taking group per span).
 * Ids: `north` (several such streets: `north1`..), the side loops `<id>_east` / `<id>_west`; order: the street loop,
 * its east side, its west side.
 *
 * ADR-0044: a north-south road that starts at the north kerb of one of these streets (Arena Avenue, from Market Street
 * up to the Sports Park) is no avenue (it is never merged with the avenue below it, which would make one column
 * through the junction). In the whole town it is driven with the district it leads into (townLoops); on its own (a
 * district slice, where it ends at the district's edge) it has no loop.
 */
function northLoops(roads: readonly Aabb[], main: Aabb): LoopPath[] {
  const cx = (r: Aabb) => (r.minX + r.maxX) / 2;
  const cz = (r: Aabb) => (r.minZ + r.maxZ) / 2;
  const eastWest = roads.filter(isEastWest);
  const spur = (r: Aabb) => eastWest.some((st) => Math.abs(r.minZ - st.maxZ) < EPS && r.minX >= st.minX - EPS && r.maxX <= st.maxX + EPS);
  const avenues = mergeColumns(roads.filter((r) => r.maxZ - r.minZ > r.maxX - r.minX && !spur(r))).sort((a, b) => cx(a) - cx(b));
  const streets = streetChains(eastWest).sort((a, b) => a.minZ - b.minZ || a.minX - b.minX);
  const used = new Set<Aabb>();
  type Spec = { centre: P2[]; laneOffset: number; suffix: string };
  const groups: Spec[][] = [];
  const mouthZ = main.maxZ + CROSSING_UTURN_SETBACK;
  for (const st of streets) {
    const below = avenues.filter((a) => !used.has(a) && Math.abs(a.maxZ - st.minZ) < EPS && a.minX >= st.minX - EPS && a.maxX <= st.maxX + EPS && a.minZ <= main.maxZ + EPS);
    if (below.length < 2) continue;
    const w = below[0]!;
    const e = below[below.length - 1]!;
    used.add(w).add(e).add(st);
    const z = cz(st);
    const laneOffset = (st.maxZ - st.minZ) / 4;
    const westOn = w.minX > st.minX + EPS;
    const eastOn = e.maxX < st.maxX - EPS;
    // v4: a corner at both ends (up the west avenue, along the street, down the east avenue); world v6: a lane loop
    // between the T-junctions, or round the corner at an end that is still one.
    const centre: P2[] = [
      ...(westOn ? [{ x: w.maxX + CROSSING_UTURN_SETBACK, z }] : [{ x: cx(w), z: mouthZ }, { x: cx(w), z }]),
      ...(eastOn ? [{ x: e.minX - CROSSING_UTURN_SETBACK, z }] : [{ x: cx(e), z }, { x: cx(e), z: mouthZ }]),
    ];
    const group: Spec[] = [{ centre, laneOffset, suffix: "" }];
    if (eastOn) group.push({ centre: [{ x: cx(e), z: mouthZ }, { x: cx(e), z }, { x: st.maxX - MAIN_UTURN_INSET, z }], laneOffset, suffix: "_east" });
    if (westOn) group.push({ centre: [{ x: cx(w), z: mouthZ }, { x: cx(w), z }, { x: st.minX + MAIN_UTURN_INSET, z }], laneOffset, suffix: "_west" });
    groups.push(group);
  }
  for (const a of avenues) if (!used.has(a)) groups.push([{ centre: [{ x: cx(a), z: mouthZ }, { x: cx(a), z: a.maxZ - DEAD_END_UTURN_INSET }], laneOffset: (a.maxX - a.minX) / 4, suffix: "" }]);
  for (const st of streets) {
    if (used.has(st)) continue;
    groups.push([{ centre: [{ x: st.minX + DEAD_END_UTURN_INSET, z: cz(st) }, { x: st.maxX - DEAD_END_UTURN_INSET, z: cz(st) }], laneOffset: (st.maxZ - st.minZ) / 4, suffix: "" }]);
  }
  return groups.flatMap((group, k) =>
    group.map((l) => {
      const id = `${groups.length === 1 ? "north" : `north${k + 1}`}${l.suffix}`;
      return l.centre.length === 2 ? laneLoop(id, l.centre[0]!, l.centre[1]!, l.laneOffset) : streetLoop(id, l.centre, l.laneOffset);
    }),
  );
}

/** Arc range around a junction span where a car is never seeded or respawned. */
const SPAN_KEEP_OUT_BEFORE = 6;
const SPAN_KEEP_OUT_AFTER = 3;

/**
 * True when arc position `s` is inside a junction or the stretch just before / after it (the respawn keep-out). A
 * traffic car held up there by a driven car waits its turn: a car crossing a junction always moves on (ADR-0006).
 */
export function nearJunction(path: LoopPath, s: number): boolean {
  return inKeepOut(path, s) !== null;
}

function inKeepOut(path: LoopPath, s: number): JunctionSpan | null {
  for (const span of path.junctions ?? []) {
    const len = span.end - span.start + SPAN_KEEP_OUT_BEFORE + SPAN_KEEP_OUT_AFTER;
    if (wrapS(path, s - (span.start - SPAN_KEEP_OUT_BEFORE)) <= len) return span;
  }
  return null;
}

/**
 * The server fleet: SERVER_FLEET cars per loop, seeded exactly like the round-2 client traffic
 * (styles cycle over VEHICLE_STYLE_COUNT, paint index modulo `colors`). Car ids are 0..n-1. A car
 * that would start inside or right before a junction is moved just past it.
 */
export function seedServerFleet(paths: readonly LoopPath[], colors: number, fleet: readonly number[] = SERVER_FLEET): CarState[] {
  const cars: CarState[] = [];
  paths.forEach((loop, i) => {
    const n = fleet[Math.min(i, fleet.length - 1)] ?? 0;
    for (const car of seedCars(i, loop, n, cars.length, 11 + i * 5, VEHICLE_STYLE_COUNT, colors)) {
      for (let guard = 0; guard < 8; guard++) {
        const span = inKeepOut(loop, car.s);
        if (!span) break;
        car.s = wrapS(loop, span.end + SPAN_KEEP_OUT_AFTER + 0.5);
      }
      cars.push(car);
    }
  });
  return cars;
}

/** Minimum distance from any walker or car when a parked car returns to traffic (ADR-0006). */
export const RESPAWN_CLEARANCE = 12;
/** Arc-length step of the respawn search (metres). */
const RESPAWN_STEP = 2;

/**
 * An arc position on loop `pathIndex` at least RESPAWN_CLEARANCE (12 m) from every walker and car,
 * outside the U-turn slow zones and away from junctions, or null if there is none. Deterministic: the
 * position with the most clearance wins (ties: the smallest arc position).
 */
export function respawnPoint(paths: readonly LoopPath[], pathIndex: number, walkers: readonly P2[], cars: readonly P2[]): number | null {
  const path = paths[pathIndex];
  if (!path) return null;
  const others = [...walkers, ...cars];
  let best: number | null = null;
  let bestClear = -Infinity;
  for (let s = 0; s < path.length; s += RESPAWN_STEP) {
    if (inSlowZone(path, s) || inKeepOut(path, s)) continue;
    const p = samplePath(path, s);
    let clear = Infinity;
    for (const o of others) clear = Math.min(clear, Math.hypot(o.x - p.x, o.z - p.z));
    if (clear < RESPAWN_CLEARANCE) continue;
    if (clear > bestClear) {
      bestClear = clear;
      best = s;
    }
  }
  return best;
}
